import { useState } from 'react';
import { FileText, Check, Scale, Landmark } from 'lucide-react';

import { cn } from '@/lib/utils';
import type {
  AssistantCaseSelection,
  AssistantCaseSelectionCandidate,
} from '@/lib/api-client';

interface CaseSelectionCardProps {
  selection: AssistantCaseSelection;
  onSelect: (candidate: AssistantCaseSelectionCandidate) => void;
}

const CASE_STATUS_LABEL: Record<string, string> = {
  active: 'Ativo',
  archived: 'Arquivado',
  suspended: 'Suspenso',
  closed: 'Encerrado',
};

/**
 * Card de desambiguacao de PROCESSO (select_case), filtrado por CLIENTE —
 * espelha o filtro em cascata da tela MANUAL de geracao de documento
 * (primeiro cliente, so depois processo, e so quando o cliente tem mais de
 * um). Mesma mecanica do ClientSelectionCard: um candidato clicavel por
 * linha; ao clicar, dispara `onSelect` (o caller reinjeta a escolha no chat,
 * pois o modelo precisa do caseId para continuar) e trava os demais
 * candidatos.
 */
export function CaseSelectionCard({ selection, onSelect }: CaseSelectionCardProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  function handleClick(candidate: AssistantCaseSelectionCandidate) {
    if (selectedId) return;
    setSelectedId(candidate.id);
    onSelect(candidate);
  }

  const hidden = selection.totalFound - selection.candidates.length;

  return (
    <div className="rounded-lg border border-[color-mix(in_oklch,var(--accent)_30%,var(--border))] bg-[var(--bg)] overflow-hidden">
      {/* Cabecalho */}
      <div className="flex items-center gap-1.5 border-b border-[var(--border)] px-3 py-2 text-xs font-semibold text-[var(--fg)] bg-[color-mix(in_oklch,var(--accent)_8%,transparent)]">
        <FileText className="h-3.5 w-3.5 text-[var(--accent)]" />
        Mais de um processo encontrado · escolha um
      </div>

      {/* Candidatos */}
      <div className="flex flex-col divide-y divide-[var(--border)]">
        {selection.candidates.map((candidate) => {
          const isSelected = selectedId === candidate.id;
          const isLocked = selectedId !== null;

          return (
            <button
              key={candidate.id}
              type="button"
              onClick={() => handleClick(candidate)}
              disabled={isLocked}
              className={cn(
                'flex flex-col items-start gap-1 px-3 py-2.5 text-left text-xs transition-colors',
                isSelected && 'bg-[color-mix(in_oklch,#16a34a_10%,transparent)]',
                !isLocked && 'hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] cursor-pointer',
                isLocked && !isSelected && 'opacity-50 cursor-not-allowed',
              )}
            >
              <div className="flex w-full flex-wrap items-center gap-x-1.5 gap-y-0.5">
                {isSelected && <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />}
                <span className="font-semibold text-[var(--fg)] break-words">
                  {candidate.numeroCnj ?? 'Sem número CNJ'}
                </span>
                <span className="text-[var(--fg-muted)]">
                  · {CASE_STATUS_LABEL[candidate.status] ?? candidate.status}
                </span>
              </div>

              {candidate.actionTypeName && (
                <span className="inline-flex items-center gap-1 text-[var(--fg-muted)] break-words">
                  <Scale className="h-3 w-3 shrink-0" />
                  {candidate.actionTypeName}
                </span>
              )}
              {(candidate.tribunalCode || candidate.vara) && (
                <span className="inline-flex items-center gap-1 text-[var(--fg-muted)] break-words">
                  <Landmark className="h-3 w-3 shrink-0" />
                  {[candidate.tribunalCode, candidate.vara].filter(Boolean).join(' · ')}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {hidden > 0 && (
        <div className="border-t border-[var(--border)] px-3 py-1.5 text-xs text-[var(--fg-muted)] italic">
          +{hidden} outro(s) processo(s). Peça para refinar se nenhum destes servir.
        </div>
      )}
    </div>
  );
}
